import { useEffect, useState, type ChangeEvent } from 'react';
import { Camera, CloudUpload, CheckCircle2, Compass, ImageOff, ImageUp, Loader2, Star, type LucideIcon } from 'lucide-react';
import type { ShownPhoto } from '../lib/area-photos';
import { armCompass, directionToBearing, isDirection, type Direction } from '../lib/compass';
import { armLocation } from '../lib/location';
import { patchFields, savePhotoDirection } from '../lib/device-db';
import { useDeviceQuery } from '../lib/hooks';
import type { PhotoPlace } from '../lib/condition-records';
import { addPhoto, AREA_PHOTO, measureOfArea, removePhoto, updateFields, type PhotoSubject } from '../lib/jobs';
import { cameraPlace, uploadPlace } from '../lib/photo-place';
import { usePhotoThumbnail } from '../lib/use-photo-images';
import { DirectionPicker } from './direction-picker';
import { PhotoPreview } from './photo-preview';
import { ErrorMessage } from './message';

/** The project cover photo: the measure's lookup to a photo row, and the text copy the older build reads. */
const COVER_LOOKUP = 'hig_coverimageid';
const COVER_TEXT = 'hig_coverphotoid';

interface ThumbProps {
  photo: ShownPhoto;
  isCover: boolean;
  onCover: () => void;
  /** Show it full screen. */
  onOpen: () => void;
}

function PhotoThumb({ photo, isCover, onCover, onOpen }: ThumbProps) {
  const [picking, setPicking] = useState(false);
  const url = usePhotoThumbnail(photo);
  const direction = photo.direction;
  const { device, row } = photo;
  // A photo's row holds its direction, so any device may change it; before the rows, only the one that took it.
  const changeable = !!row || !!device;

  function pick(next: Direction | null) {
    setPicking(false);
    if (row) void patchFields(row.id, { hig_bearing: next === null ? null : directionToBearing(next) });
    else if (device) void savePhotoDirection(device, next);
  }

  const status = photo.missing
    ? 'Not on the job’s SharePoint site any more: restore it from the recycle bin, or remove it'
    : !photo.uploaded
      ? 'Waiting to upload'
      : device
        ? 'On SharePoint'
        : 'On SharePoint, taken on another device';
  const directionLabel = (
    <>
      <Compass className="h-3 w-3" /> {direction ?? '–'}
    </>
  );
  const directionClass = 'absolute bottom-1 left-1 flex items-center gap-0.5 rounded bg-white/90 px-1 text-[10px] font-medium text-slate-700';

  return (
    <figure className="relative select-none">
      {/* Under the star and compass, which sit on top and keep their own taps. */}
      <button type="button" onClick={onOpen} aria-label={`Show full screen: ${photo.fileName}`} className="block rounded">
        {url ? (
          <img src={url} alt={photo.fileName} draggable={false} className="h-20 w-20 rounded object-cover [-webkit-touch-callout:none]" />
        ) : (
          <div className="flex h-20 w-20 items-center justify-center rounded bg-slate-100 text-slate-400" title={url === null ? 'Can’t load it from SharePoint now — try again with signal' : 'Loading from SharePoint'}>
            {url === null ? <ImageOff className="h-5 w-5" /> : <Loader2 className="h-5 w-5 animate-spin" />}
          </div>
        )}
      </button>
      <button type="button" onClick={onCover} aria-label={`${isCover ? 'Cover photo' : 'Make the cover photo'}: ${photo.fileName}`} className="absolute left-0 top-0 rounded-br bg-white/85 p-1">
        <Star className={`h-4 w-4 ${isCover ? 'fill-primary text-primary' : 'text-slate-500'}`} />
      </button>
      <span className="absolute bottom-1 right-1 rounded-full bg-white/90 p-0.5" title={status} aria-label={status}>
        {photo.missing ? (
          <ImageOff className="h-3.5 w-3.5 text-red-700" />
        ) : photo.uploaded ? (
          <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />
        ) : (
          <CloudUpload className="h-3.5 w-3.5 text-amber-600" />
        )}
      </span>
      {changeable ? (
        <button type="button" onClick={() => setPicking((p) => !p)} aria-label={`Camera facing ${direction ?? 'not set'}: ${photo.fileName}`} className={directionClass}>
          {directionLabel}
        </button>
      ) : (
        <span aria-label={`Camera facing ${direction ?? 'not set'}: ${photo.fileName}`} className={directionClass}>
          {directionLabel}
        </span>
      )}
      {picking && changeable && <DirectionPicker value={isDirection(direction) ? direction : null} onPick={pick} onClose={() => setPicking(false)} />}
      <figcaption className="mt-0.5 w-20 truncate text-[10px] text-slate-500">{photo.fileName}</figcaption>
    </figure>
  );
}

interface AddTileProps {
  icon: LucideIcon;
  label: string;
  busy: boolean;
  /** Open the camera rather than the photo library or files. */
  camera?: boolean;
  /** Choose one photo at a time (the feature photo). */
  single?: boolean;
  onFiles: (files: File[]) => void;
}

/** A tile that adds photos: the camera, or the photo library and files. */
function AddPhotoTile({ icon: Icon, label, busy, camera, single, onFiles }: AddTileProps) {
  function chosen(event: ChangeEvent<HTMLInputElement>) {
    const files = Array.from(event.target.files ?? []);
    event.target.value = '';
    if (files.length > 0) onFiles(files);
  }

  return (
    <label className="flex h-20 w-20 cursor-pointer flex-col items-center justify-center rounded border-2 border-dashed border-slate-300 text-center text-xs text-slate-500">
      {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Icon className="h-5 w-5" />}
      <span className="mt-1">{label}</span>
      {/* `capture` is what makes it open the camera; without it the iPad offers Photos and Files. */}
      <input type="file" accept="image/*" capture={camera ? 'environment' : undefined} multiple={!single} onChange={chosen} className="hidden" disabled={busy} />
    </label>
  );
}

interface PhotoStripProps {
  jobId: string;
  areaId: string;
  /** The surface these photos are of; AREA_PHOTO = the area's own shot, one at a time; null = the area in general ("General 01.jpg"). */
  surface: PhotoSubject;
  photos: ShownPhoto[];
}

/** Photos and the Take photo / Upload photo tiles, for an area or one of its surfaces. */
export function PhotoStrip({ jobId, areaId, surface, photos }: PhotoStripProps) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previewing, setPreviewing] = useState<number | null>(null);
  const measure = useDeviceQuery(() => measureOfArea(areaId), [areaId]);
  const coverId = measure?.fields[COVER_LOOKUP] ?? measure?.fields[COVER_TEXT] ?? null;
  // The feature photo: a new one replaces the one shown (which stays among the area's photos).
  const single = surface === AREA_PHOTO;
  const replacing = single && photos.length > 0;

  // The compass needs a tap to start (iOS asks permission then).
  useEffect(armCompass, []);
  // Every photo records where it was taken, so one from measuring can be pinned on a later site plan (DD, 2026-10-21).
  useEffect(armLocation, []);

  /** Save each photo the same way, taken or chosen; only where, which way and when differ (photo-place.ts). */
  async function addPhotos(files: File[], placeOf: (file: File) => Promise<PhotoPlace>) {
    setBusy(true);
    setError(null);
    try {
      for (const file of files) await addPhoto(jobId, areaId, file, surface, await placeOf(file));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  function toggleCover(photo: ShownPhoto) {
    if (!measure) return;
    // A photo from before the rows can only be the text copy's cover.
    const cover = coverId === photo.id ? { [COVER_LOOKUP]: null, [COVER_TEXT]: null } : { [COVER_LOOKUP]: photo.row ? photo.id : null, [COVER_TEXT]: photo.id };
    void updateFields(measure, cover);
  }

  return (
    <div>
      <div className="flex flex-wrap gap-3">
        {photos.map((photo, i) => (
          <PhotoThumb key={photo.id} photo={photo} isCover={photo.id === coverId} onCover={() => toggleCover(photo)} onOpen={() => setPreviewing(i)} />
        ))}
        <div className="flex gap-3">
          <AddPhotoTile icon={Camera} label={replacing ? 'Take new' : 'Take photo'} camera single={single} busy={busy} onFiles={(files) => void addPhotos(files, cameraPlace)} />
          <AddPhotoTile icon={ImageUp} label={replacing ? 'Upload new' : 'Upload photo'} single={single} busy={busy} onFiles={(files) => void addPhotos(files, uploadPlace)} />
        </div>
      </div>
      {error && <ErrorMessage className="mt-2">{error}</ErrorMessage>}
      <PhotoPreview photos={photos} index={previewing} onIndex={setPreviewing} onRemove={(photo) => photo.row && void removePhoto(photo.row)} />
    </div>
  );
}
